// =============================================
// Reconcile a vendor / customer / other party's balance  (acctg_party_reconcile, migration 125)
// =============================================
// The party's ledger in one set of books — its opening balance, then every posted journal
// line naming it — next to the balance on the party's own statement. Lines that appear on
// the statement are ticked off; what is ticked (now or in an earlier reconciliation) plus
// the opening balance should equal the statement balance. Saving records the
// reconciliation and stamps the ticked lines with it; the newest one can be undone.
// The maths is utils/partyLedger.ts (pure, unit-tested).
// =============================================
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiX } from 'react-icons/fi';
import { loadTable, saveRow, addRow, getTableData } from '../../db-viewer/data/dataAccess';
import { OwnerEntity } from '../utils/ownerEntities';
import { buildPartyLedger, reconcileSummary, LedgerPartyKind } from '../utils/partyLedger';

type Row = Record<string, any>;

interface PartyReconcileModalProps {
  kind: LedgerPartyKind;
  partyId: number;
  partyName: string;
  manualOpening: number | string | null;
  manualOpeningDate: string | null;
  accountOwnerId: number;
  userId: number | null;
  books: OwnerEntity[];              // the real sets of books the party can have a ledger in
  defaultBooksKey?: string;
  currency?: string;
  onClose: () => void;
}

const TODAY = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const NOW_ISO = () => new Date().toISOString();
const money = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const PartyReconcileModal: React.FC<PartyReconcileModalProps> = ({
  kind, partyId, partyName, manualOpening, manualOpeningDate, accountOwnerId, userId, books, defaultBooksKey, currency, onClose,
}) => {
  const [loaded, setLoaded] = useState(false);
  const [journals, setJournals] = useState<Row[]>([]);
  const [lines, setLines] = useState<Row[]>([]);
  const [reconciles, setReconciles] = useState<Row[]>([]);

  const keyOf = (e: OwnerEntity) => `${e.owner_type}:${e.owner_id}`;
  const [booksKey, setBooksKey] = useState(defaultBooksKey && books.some(e => keyOf(e) === defaultBooksKey) ? defaultBooksKey : (books[0] ? keyOf(books[0]) : ''));
  const [statementDate, setStatementDate] = useState(TODAY());
  const [statementBalance, setStatementBalance] = useState('');
  const [note, setNote] = useState('');
  const [ticked, setTicked] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [confirmClose, setConfirmClose] = useState(false);

  const load = useCallback(async () => {
    const safe = (t: string) => loadTable(t).catch(() => getTableData(t));
    const [j, l, r] = await Promise.all([safe('acctg_gl_journal'), safe('acctg_gl_journal_line'), safe('acctg_party_reconcile')]);
    setJournals(j); setLines(l); setReconciles(r); setLoaded(true);
  }, []);
  useEffect(() => { load(); }, [load]);

  const entity = books.find(e => keyOf(e) === booksKey) || null;
  const ledger = useMemo(() => buildPartyLedger({
    journals: journals as any[], lines: lines as any[], kind, partyId,
    books: entity ? { account_owner_id: accountOwnerId, owner_type: entity.owner_type, owner_id: entity.owner_id } : null,
    manualOpening, manualOpeningDate,
  }), [journals, lines, kind, partyId, entity, accountOwnerId, manualOpening, manualOpeningDate]);

  const statement = statementBalance.trim() === '' ? NaN : Number(statementBalance.replace(/,/g, ''));
  const summary = useMemo(() => reconcileSummary(ledger, ticked, Number.isFinite(statement) ? statement : 0), [ledger, ticked, statement]);
  const canTick = (r: { date: string; reconcile_id: number | null }) => r.reconcile_id == null && (!statementDate || r.date <= statementDate);

  // Past reconciliations of this party in these books, newest first.
  const history = useMemo(() => reconciles
    .filter(r =>
      // eslint-disable-next-line eqeqeq
      r.is_deleted != 1 && r.party_kind === kind && r.party_id == partyId && r.account_owner_id == accountOwnerId &&
      // eslint-disable-next-line eqeqeq
      entity && r.owner_type === entity.owner_type && r.owner_id == entity.owner_id)
    .sort((a, b) => String(b.statement_date || '').localeCompare(String(a.statement_date || '')) || Number(b.reconcile_id) - Number(a.reconcile_id)),
  [reconciles, kind, partyId, accountOwnerId, entity]);

  const toggle = (id: number) => { setTicked(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; }); setMsg(''); };
  const tickAll = () => setTicked(new Set(ledger.rows.filter(canTick).map(r => r.line_id)));

  const switchBooks = (key: string) => { setBooksKey(key); setTicked(new Set()); setMsg(''); };
  // Lines after the statement date cannot be on that statement.
  const changeDate = (v: string) => {
    setStatementDate(v);
    setTicked(prev => new Set(ledger.rows.filter(r => prev.has(r.line_id) && (!v || r.date <= v)).map(r => r.line_id)));
  };

  const save = useCallback(async () => {
    if (!entity || busy) return;
    if (!statementDate) { setMsg('Enter the statement date.'); return; }
    if (!Number.isFinite(statement)) { setMsg('Enter the balance shown on the statement.'); return; }
    setBusy(true);
    try {
      const now = NOW_ISO();
      const rec = await addRow('acctg_party_reconcile', {
        account_owner_id: accountOwnerId, owner_type: entity.owner_type, owner_id: entity.owner_id,
        party_kind: kind, party_id: partyId,
        statement_date: statementDate, statement_balance: statement,
        book_balance: summary.cleared, difference: summary.difference, line_count: summary.tickedCount,
        note: note.trim() || null,
        is_active: 1, is_deleted: 0, created_by: userId, created_at: now, updated_at: now,
      }, 'reconcile_id');
      const recId = Number(rec.reconcile_id);
      for (const id of Array.from(ticked)) {
        await saveRow('acctg_gl_journal_line', id, { reconcile_id: recId, updated_at: now }, 'line_id');
      }
      await load();
      setTicked(new Set()); setNote(''); setConfirmClose(false);
      setMsg(summary.difference === 0
        ? `Reconciled to ${statementDate} — the books agree with the statement.`
        : `Saved with a difference of ${money(Math.abs(summary.difference))} still to find.`);
    } catch (e: any) {
      setMsg(e?.message ? `Could not save: ${e.message}` : 'Could not save the reconciliation. Please try again.');
    } finally { setBusy(false); }
  }, [entity, busy, statementDate, statement, accountOwnerId, kind, partyId, summary, note, userId, ticked, load]);

  // Undo the newest reconciliation: its lines are open again; the record is kept, marked undone.
  const undo = useCallback(async (rec: Row) => {
    if (busy) return;
    setBusy(true);
    try {
      const now = NOW_ISO();
      // eslint-disable-next-line eqeqeq
      for (const l of lines.filter(x => x.reconcile_id != null && x.reconcile_id == rec.reconcile_id)) {
        await saveRow('acctg_gl_journal_line', l.line_id, { reconcile_id: null, updated_at: now }, 'line_id');
      }
      await saveRow('acctg_party_reconcile', rec.reconcile_id, { is_deleted: 1, updated_at: now }, 'reconcile_id');
      await load();
      setMsg(`Reconciliation to ${String(rec.statement_date || '').slice(0, 10)} undone.`);
    } catch (e: any) {
      setMsg(e?.message ? `Could not undo: ${e.message}` : 'Could not undo. Please try again.');
    } finally { setBusy(false); }
  }, [busy, lines, load]);

  const tryClose = () => { if (ticked.size > 0 && !confirmClose) setConfirmClose(true); else onClose(); };

  const grows = kind === 'customer' ? 'Invoiced' : 'Billed';
  const shrinks = kind === 'customer' ? 'Received' : 'Paid';

  return (
    <div
      className="pm-doc-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={`Reconcile ${partyName}`}
      onMouseDown={e => { if (e.target === e.currentTarget) tryClose(); }}
      onKeyDown={e => { if (e.key === 'Escape') tryClose(); }}
    >
      <div className="pm-doc-modal pm-rec-modal">
        <div className="pm-doc-head">
          <span className="pm-form-title">Reconcile balance — {partyName}</span>
          <button type="button" className="pm-icon-btn" title="Close" autoFocus onClick={tryClose}><FiX size={18} /></button>
        </div>

        <div className="pm-rec-body">
          {!loaded ? <div className="pm-empty">Loading…</div> : !entity ? <div className="pm-empty">No books to reconcile in.</div> : (
            <>
              <div className="pm-rec-controls">
                {books.length > 1 && (
                  <label className="pm-field">
                    <span className="pm-label">Books of</span>
                    <select className="pm-input" value={booksKey} onChange={e => switchBooks(e.target.value)}>
                      {books.map(e => <option key={keyOf(e)} value={keyOf(e)}>{e.display_name}</option>)}
                    </select>
                  </label>
                )}
                <label className="pm-field">
                  <span className="pm-label">Statement date</span>
                  <input className="pm-input" type="date" max="9999-12-31" value={statementDate} onChange={e => changeDate(e.target.value)} />
                </label>
                <label className="pm-field" title={`The closing balance printed on ${partyName}'s statement`}>
                  <span className="pm-label">Statement balance{currency ? ` (${currency})` : ''}</span>
                  <input className="pm-input" type="text" inputMode="decimal" placeholder="0.00" value={statementBalance} onChange={e => { setStatementBalance(e.target.value); setMsg(''); }} />
                </label>
                <label className="pm-field pm-rec-note">
                  <span className="pm-label">Note</span>
                  <input className="pm-input" type="text" value={note} placeholder="Optional" onChange={e => setNote(e.target.value)} />
                </label>
              </div>

              <div className="pm-rec-summary">
                <span>Opening <strong>{money(ledger.opening)}</strong>
                  <em>{ledger.openingFrom === 'journal' ? ` from ${ledger.openingRef}` : ledger.openingFrom === 'manual' ? ' typed in Masters' : ''}</em>
                </span>
                <span>Books say <strong>{money(ledger.balance)}</strong></span>
                <span>Ticked off <strong>{money(summary.cleared)}</strong></span>
                <span>Not on statement yet <strong>{money(summary.outstanding)}</strong></span>
                <span className={!Number.isFinite(statement) ? '' : summary.difference === 0 ? 'pm-rec-ok' : 'pm-rec-off'}>
                  Difference <strong>{Number.isFinite(statement) ? money(summary.difference) : '—'}</strong>
                </span>
              </div>

              {ledger.rows.length === 0 ? (
                <div className="pm-empty">
                  No journal lines name {partyName} in these books yet. In Make Journal Entries, pick it in the <strong>Party</strong> box
                  of a line — its bills and payments then show here to tick off against the statement.
                </div>
              ) : (
                <div className="pm-table-wrap pm-rec-lines">
                  <table className="pm-table">
                    <thead>
                      <tr>
                        <th><button type="button" className="pm-link-btn" title="Tick every open line up to the statement date" onClick={tickAll}>All</button></th>
                        <th>Date</th><th>Entry</th><th>Memo</th>
                        <th className="pm-num">{grows}</th><th className="pm-num">{shrinks}</th><th className="pm-num">Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledger.rows.map(r => (
                        <tr key={r.line_id} className={r.reconcile_id != null ? 'pm-row-inactive' : ''}>
                          <td>
                            {r.reconcile_id != null
                              ? <span className="pm-badge" title="Ticked off in an earlier reconciliation">Reconciled</span>
                              : <input type="checkbox" aria-label={`On the statement: ${r.ref} ${money(Math.abs(r.amount))}`} checked={ticked.has(r.line_id)} disabled={!canTick(r)} onChange={() => toggle(r.line_id)} />}
                          </td>
                          <td>{r.date}</td><td>{r.ref}</td><td>{r.memo}</td>
                          <td className="pm-num">{r.amount > 0 ? money(r.amount) : ''}</td>
                          <td className="pm-num">{r.amount < 0 ? money(-r.amount) : ''}</td>
                          <td className="pm-num">{money(r.balance)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {history.length > 0 && (
                <div className="pm-rec-history">
                  <span className="pm-label">Earlier reconciliations</span>
                  {history.map((h, i) => (
                    <div className="pm-rec-history-row" key={h.reconcile_id}>
                      <span>{String(h.statement_date || '').slice(0, 10)}</span>
                      <span>statement {money(Number(h.statement_balance) || 0)}</span>
                      <span className={Number(h.difference) === 0 ? 'pm-rec-ok' : 'pm-rec-off'}>
                        {Number(h.difference) === 0 ? 'reconciled' : `difference ${money(Math.abs(Number(h.difference) || 0))}`}
                      </span>
                      <span className="pm-hint">{Number(h.line_count) || 0} line{Number(h.line_count) === 1 ? '' : 's'}{h.note ? ` · ${h.note}` : ''}</span>
                      {i === 0 && <button type="button" className="pm-link-btn" disabled={busy} title="Open this reconciliation's lines again" onClick={() => undo(h)}>Undo</button>}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className="pm-rec-foot">
          {confirmClose
            ? <span className="pm-error">Ticked lines are not saved yet.</span>
            : <span className={msg ? 'pm-msg' : 'pm-hint'}>{msg || 'Tick the lines that appear on the statement, then save.'}</span>}
          <span className="pm-rec-foot-actions">
            {confirmClose && <button type="button" className="pm-btn" onClick={onClose}>Close without saving</button>}
            <button type="button" className="pm-btn pm-btn-primary" disabled={busy || !loaded || !entity} onClick={save}>{busy ? 'Saving…' : 'Save reconciliation'}</button>
            {!confirmClose && <button type="button" className="pm-btn" onClick={tryClose}>Close</button>}
          </span>
        </div>
      </div>
    </div>
  );
};

export default PartyReconcileModal;
